import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, initials } from "@/lib/helpers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Search, Plus, Pencil, Trash2, UserPlus, LayoutGrid, List, Loader2 } from "lucide-react";

export const STATUS = {
  pendiente: { label: "Pendiente", cls: "bg-slate-100 text-slate-700 border border-slate-200" },
  "en formacion": { label: "En formación", cls: "bg-sky-100 text-sky-800 border border-sky-200" },
  aprobado: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
  suspenso: { label: "Suspenso", cls: "bg-red-100 text-red-800 border border-red-200" },
  fichaje: { label: "Fichaje", cls: "bg-accent/10 text-accent border border-accent/30" },
  baja: { label: "Baja", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
};
const EMPTY = { name: "", dni: "", phone: "", email: "", start_date: "", end_date: "", status: "pendiente", center: "", hours: "", subsidized: false, notes: "" };

function StudentDialog({ open, student, isAdmin, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(student ? { ...EMPTY, ...student } : EMPTY); }, [open, student]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      if (student) await api.put(`/students/${student.id}`, form); else await api.post("/students", form);
      toast.success(student ? "Alumno actualizado" : "Alumno creado"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const F = ({ k, l, type = "text", full }) => (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label>{l}</Label>
      <Input type={type} value={form[k] || ""} disabled={!isAdmin} onChange={(e) => set(k, e.target.value)} data-testid={`student-${k}-input`} /></div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[95vh] overflow-y-auto p-4 sm:p-6" data-testid="student-dialog">
        <DialogHeader><DialogTitle className="font-display">{student ? (isAdmin ? "Editar alumno" : "Ficha del alumno") : "Nuevo alumno"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <F k="name" l="Nombre completo *" full />
          <F k="dni" l="DNI / NIE" /><F k="phone" l="Teléfono" />
          <F k="email" l="Email" type="email" full />
          <F k="start_date" l="Fecha de inicio" type="date" /><F k="end_date" l="Fecha de fin" type="date" />
          <div className="space-y-1.5"><Label>Estado</Label>
            <select className="h-10 w-full rounded-md border bg-white px-3 text-sm" value={form.status} disabled={!isAdmin} onChange={(e) => set("status", e.target.value)} data-testid="student-status-select">
              {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
            </select></div>
          <F k="center" l="Centro de formación" />
          <F k="hours" l="Horas del curso" />
          <div className="flex items-center gap-3 pt-6"><input type="checkbox" id="st-bon" className="h-4 w-4 accent-primary" checked={!!form.subsidized} disabled={!isAdmin}
            onChange={(e) => set("subsidized", e.target.checked)} data-testid="student-subsidized-checkbox" /><Label htmlFor="st-bon">Curso bonificado</Label></div>
          <F k="notes" l="Notas" full />
        </div>
        <DialogFooter className="flex-row flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>{isAdmin ? "Cancelar" : "Cerrar"}</Button>
          {isAdmin && <Button onClick={save} disabled={saving} data-testid="save-student-button">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Guardar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Students() {
  const { isAdmin } = useAuth();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("todos");
  const [mode, setMode] = useState(() => localStorage.getItem("stu_view_mode") || "list");
  const [dialog, setDialog] = useState({ open: false, student: null });
  const [hiring, setHiring] = useState(null);
  const [hireForm, setHireForm] = useState({ hire_date: new Date().toISOString().slice(0, 10), position: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { localStorage.setItem("stu_view_mode", mode); }, [mode]);

  const load = () => api.get("/students").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (!list) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div>;

  const filtered = list.filter((s) => (status === "todos" || s.status === status) &&
    (s.name.toLowerCase().includes(q.toLowerCase()) || (s.dni || "").toLowerCase().includes(q.toLowerCase()) || (s.center || "").toLowerCase().includes(q.toLowerCase())));

  const del = async (s) => {
    if (!window.confirm(`¿Eliminar al alumno ${s.name}?`)) return;
    try { await api.delete(`/students/${s.id}`); toast.success("Alumno eliminado"); load(); } catch { toast.error("No se pudo eliminar"); }
  };
  const hire = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/students/${hiring.id}/hire`, hireForm);
      toast.success(`${r.data.name} pasado a empleados`); setHiring(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const Actions = ({ s }) => (
    <span className="flex gap-1 justify-end">
      {isAdmin && !s.hired_employee_id && (
        <Button size="sm" variant="outline" className="h-8" onClick={() => { setHiring(s); setHireForm({ hire_date: new Date().toISOString().slice(0, 10), position: "" }); }} data-testid={`hire-student-${s.id}`}>
          <UserPlus className="h-4 w-4 mr-1" /> Contratar</Button>
      )}
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDialog({ open: true, student: s })} data-testid={`view-student-${s.id}`}><Pencil className="h-4 w-4" /></Button>
      {isAdmin && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(s)} data-testid={`delete-student-${s.id}`}><Trash2 className="h-4 w-4" /></Button>}
    </span>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Alumnos en Formación</h1>
          <p className="text-muted-foreground text-sm mt-1">{list.length} alumnos · {list.filter((s) => s.status === "en formacion").length} en formación · {list.filter((s) => s.hired_employee_id).length} contratados.</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-60">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Buscar nombre, DNI, centro" value={q} onChange={(e) => setQ(e.target.value)} data-testid="student-search" />
          </div>
          <select className="h-10 rounded-md border bg-white px-3 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="student-status-filter">
            <option value="todos">Todos los estados</option>
            {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </select>
          <div className="flex rounded-md border overflow-hidden" data-testid="student-view-toggle">
            <button onClick={() => setMode("grid")} data-testid="student-view-grid" className={`px-2.5 ${mode === "grid" ? "bg-primary text-white" : "bg-white text-slate-600"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button onClick={() => setMode("list")} data-testid="student-view-list" className={`px-2.5 ${mode === "list" ? "bg-primary text-white" : "bg-white text-slate-600"}`}><List className="h-4 w-4" /></button>
          </div>
          {isAdmin && <Button onClick={() => setDialog({ open: true, student: null })} data-testid="add-student-button"><Plus className="h-4 w-4 mr-2" /> Nuevo alumno</Button>}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground text-sm"><GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-40" /> No hay alumnos.</Card>
      ) : mode === "list" ? (
        <Card className="overflow-x-auto" data-testid="students-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>
              <th className="text-left px-4 py-3">Alumno</th><th className="text-left px-4 py-3">DNI</th><th className="text-left px-4 py-3 hidden md:table-cell">Teléfono</th>
              <th className="text-left px-4 py-3">Curso</th><th className="text-left px-4 py-3 hidden lg:table-cell">Centro</th><th className="text-center px-4 py-3 hidden md:table-cell">Horas</th>
              <th className="text-left px-4 py-3">Estado</th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y">
              {filtered.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50" data-testid={`student-row-${s.id}`}>
                  <td className="px-4 py-2.5"><p className="font-medium">{s.name}</p><p className="text-xs text-muted-foreground truncate">{s.email || "—"}</p></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{s.dni || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs hidden md:table-cell">{s.phone || "—"}</td>
                  <td className="px-4 py-2.5 text-xs font-mono">{fmtDate(s.start_date)} → {fmtDate(s.end_date)}</td>
                  <td className="px-4 py-2.5 hidden lg:table-cell">{s.center || "—"}</td>
                  <td className="px-4 py-2.5 text-center hidden md:table-cell">{s.hours || "—"}{s.subsidized && <span className="ml-1 text-[10px] text-emerald-700">bonif.</span>}</td>
                  <td className="px-4 py-2.5"><Badge className={`${STATUS[s.status]?.cls} text-[10px]`}>{STATUS[s.status]?.label || s.status}</Badge></td>
                  <td className="px-4 py-2.5"><Actions s={s} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="students-grid">
          {filtered.map((s) => (
            <Card key={s.id} className="p-5 flex flex-col gap-3" data-testid={`student-card-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center font-semibold text-accent shrink-0">{initials(s.name)}</div>
                <div className="min-w-0 flex-1"><p className="font-display font-bold truncate">{s.name}</p><p className="text-xs text-muted-foreground font-mono">{s.dni || "—"} · {s.phone || "—"}</p></div>
                <Badge className={`${STATUS[s.status]?.cls} text-[10px] shrink-0`}>{STATUS[s.status]?.label}</Badge>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>{s.center || "Sin centro"} · {s.hours || "—"} h {s.subsidized ? "· bonificado" : ""}</p>
                <p className="font-mono">{fmtDate(s.start_date)} → {fmtDate(s.end_date)}</p>
              </div>
              <div className="pt-2 border-t"><Actions s={s} /></div>
            </Card>
          ))}
        </div>
      )}

      <StudentDialog open={dialog.open} student={dialog.student} isAdmin={isAdmin} onClose={() => setDialog({ open: false, student: null })} onSaved={load} />

      <Dialog open={!!hiring} onOpenChange={(o) => !o && setHiring(null)}>
        <DialogContent className="max-w-md w-[calc(100vw-1rem)] sm:w-full" data-testid="hire-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><UserPlus className="h-5 w-5 text-accent" /> Pasar a empleados</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Se creará la ficha de empleado de <b>{hiring?.name}</b> con sus datos (DNI, teléfono, email) y su curso de formación. El alumno quedará como "Fichaje".</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Fecha de alta</Label><Input type="date" value={hireForm.hire_date} onChange={(e) => setHireForm({ ...hireForm, hire_date: e.target.value })} data-testid="hire-date-input" /></div>
            <div className="space-y-1.5"><Label>Puesto</Label><Input value={hireForm.position} placeholder="Instalador…" onChange={(e) => setHireForm({ ...hireForm, position: e.target.value })} data-testid="hire-position-input" /></div>
          </div>
          <DialogFooter className="flex-row flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={() => setHiring(null)}>Cancelar</Button>
            <Button onClick={hire} disabled={busy} data-testid="confirm-hire-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crear empleado</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
